import axios from 'axios';

import getAuthSession from './auth';
import User, { BaseUser, Company, InviteUser } from '../interfaces/users.interface';
import { UsersResponse } from '../app/store/usersSlice';
import { updateCompanyBoolean } from './company';
import { sendAdmitEmployeeNotice, sendRejectEmployeeNotice } from './notifications';
import { GridSortModel } from '@mui/x-data-grid/models/gridSortModel';
import { GridFilterModel } from '@mui/x-data-grid/models/gridFilterModel';
import { GetDataGridParams } from './companyCertifications';

export const USER_TYPES = {
  TPN_ADMIN: 'tpn_admin',
  VENDOR_ADMIN: 'vendor_admin',
  CONTENT_OWNER: 'content_owner',
  ASSESSOR: 'assessor',
};

const GRID_TO_API_MAP: Record<string, string> = {
  company: 'company',
  companyType: 'company_type',
  firstName: 'first_name',
  lastName: 'last_name',
  createdAt: 'date_joined',
  lastLogin: 'last_login',
  isCognitoUserActive: 'is_cognito_user_active',
};

export const fetchTPNAdmins = async () => {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/auth/manage-users/?type=tpn_admin&limit=99999`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as UsersResponse;
};
export interface DataGridParams {
  page: number;
  pageSize: number;
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
}

export interface UsersDataGridResponse {
  results: User[];
  count: number;
}

export async function processAdminUsersReportRequest() {
  const authSession = await getAuthSession();
  const token = authSession.getIdToken().getJwtToken();

  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/users-data-grid/`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  return response.data;
}

export async function getUsersForDataGrid({
  page,
  pageSize,
  sortModel,
  filterModel,
}: DataGridParams): Promise<UsersDataGridResponse> {
  const authSession = await getAuthSession();

  const sort = sortModel?.[0];
  const params: Record<string, any> = {
    limit: pageSize,
    offset: page * pageSize,
  };

  if (sort?.field && sort?.sort) {
    const apiField = GRID_TO_API_MAP[sort.field] || sort.field;
    params.ordering = sort.sort === 'asc' ? apiField : `-${apiField}`;
  }

  if (filterModel?.items?.length) {
    filterModel.items.forEach((item) => {
      if (!item.value) return;

      const apiField = GRID_TO_API_MAP[item.columnField] || item.columnField;

      if (item.columnField === 'isCognitoUserActive') {
        params[apiField] = item.value === 'Yes' || item.value === true;
      } else {
        params[apiField] = item.value;
      }
    });
  }

  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/users-data-grid/`,
    {
      headers: {
        Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
      params,
    },
  );

  return {
    results: response.data.results as User[],
    count: response.data.count,
  };
}

export default async function createUser(user: BaseUser | InviteUser) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/auth/manage-users/`,
    { ...user },
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as User;
}

export async function updateUser(user: BaseUser) {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/users/${user.id}/`,
    { ...user },
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as User;
}

export async function getUser(userId: number) {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/users/${userId}/`,
    {
      headers: {
        'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );
  return response.data as User;
}

export async function getUserFromAuthSession() {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/auth/cognito-token/`,
    {
      headers: {
        'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );
  return response.data as User;
}

export async function resetUser(user: User) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/auth/cognito/reset-user/${user.id}/`,
    {},
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function resendUserInvite(user: User) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/auth/cognito/resend-invite/${user.id}/`,
    {},
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function deleteUser(user: User) {
  const authSession = await getAuthSession();
  const response = await axios.delete(
    `${process.env.REACT_APP_BASE_API}/auth/manage-users/${user.id}/`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function getAssessorsForDataGrid({
  page,
  pageSize,
  sortModel,
  filterModel,
}: GetDataGridParams): Promise<{ results: User[]; count: number }> {
  const authSession = await getAuthSession();

  const params: Record<string, any> = {
    limit: pageSize,
    offset: page * pageSize,
  };

  const sort = sortModel?.[0];
  if (sort?.field && sort?.sort) {
    params.ordering = sort.sort === 'asc' ? sort.field : `-${sort.field}`;
  }

  params.type = USER_TYPES.ASSESSOR;
  if (filterModel?.items?.length) {
    filterModel.items.forEach(item => {
      const value = item.value;
      if (value === undefined || value === null || String(value).trim() === '') return;
      if (!item.columnField) return;

      params[item.columnField] = value;
    });
  }

  const response = await axios.get(`${process.env.REACT_APP_BASE_API}/users/`, {
    headers: {
      Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
    },
    params,
  });

  return {
    results: response.data.results as User[],
    count: response.data.count,
  };
}

export const setPendingUser = async (user: number, company: number) => {
  const authSession = await getAuthSession();

  return Promise.all([
    axios.patch(
      `${process.env.REACT_APP_BASE_API}/users/${user}/`,
      { id: user, pendingCompanies: [company] },
      {
        headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
      },
    ),
    axios.post(
      `${process.env.REACT_APP_BASE_API}/companies/${company}/pending-employees/${user}/`,
      {},
      {
        headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
      },
    ),
  ]).then(res => {
    return res;
  });
};

export const handlePendingUser = async (user: number, company: Company, action: 'accept' | 'reject') => {
  const employees = company.employees.map(employee => employee.id);
  if (action === 'accept') employees.push(user);

  const newCompany = await updateCompanyBoolean({ id: company.id, pendingEmployees: company.pendingEmployees.filter(pending => pending.id !== user).map(pending => pending.id), employees });

  if (!company?.id) return newCompany;

  if (action === 'accept') {
    sendAdmitEmployeeNotice(company.id, user).then(() => {});
  } else {
    sendRejectEmployeeNotice(company.id, user).then(() => {});
  }

  return newCompany;
};

export const getHelpURL = (userType?:string) => {
  let baseURL = 'https://tpnplus.atlassian.net/servicedesk/customer/portal/3';
  if (userType === USER_TYPES.ASSESSOR) return baseURL + '/group/11';
  if (userType === USER_TYPES.VENDOR_ADMIN) return baseURL + '/group/9';
  if (userType === USER_TYPES.CONTENT_OWNER) return baseURL + '/group/10';
  return baseURL;
};

export const deactivateCompanyUser = async (user: User, company: Company) => {
  const authSession = await getAuthSession();
  await axios.post(
    `${process.env.REACT_APP_BASE_API}/deactivate-company-user/${company.id}/${user.id}/`,
    {},
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return null;
};
